
import { expressions, regexValidators } from "../../survey/validators";
import { translate } from "../../lib/intl";

// Most fields are optional; only fields with explicit `isRequired: true` are required.
const required = false;

const choice = (value, en, rw = en) => ({ value, text: { en, rw } });

const cropChoices = [
  choice("avocado", "Avocado"),
  choice("banana", "Banana"),
  choice("beans", "Beans"),
  choice("carrots", "Carrots"),
  choice("cassava", "Cassava"),
  choice("chili", "Chili"),
  choice("coffee", "Coffee"),
  choice("fish", "Fish"),
  choice("horticulture", "Horticulture"),
  choice("maize", "Maize"),
  choice("onion", "Onion"),
  choice("orange_sweet_potato", "Orange sweet potato"),
  choice("passion_fruit", "Passion fruit"),
  choice("potato", "Potato"),
  choice("pyrethrum", "Pyrethrum"),
  choice("rice", "Rice"),
  choice("soybean", "Soybean"),
  choice("tea", "Tea"),
  choice("tomato", "Tomato"),
  choice("wheat", "Wheat"),
];

const generalInformation = (clientUrlForCatalog) => ({
  name: "page1",
  title: {
    en: "Cooperative general information",
    rw: "Amakuru rusange y'ishyirahamwe",
  },
  elements: [
    {
      type: "expression",
      name: "cooperative_name",
      title: {
        en: "Name of the cooperative",
        rw: "Izina ry'ishyirahamwe",
      },
      expression: "{cooperative_name}",
      includeIntoResult: false,
    },
    {
      name: "province",
      type: "dropdown",
      title: {
        en: "Province where the cooperative is located",
        rw: "Intara ishyirahamwe riherereyemo",
      },

      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/provinces/`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Idprovince", visible: false },
    {
      name: "district",
      type: "dropdown",
      title: {
        en: "District where the cooperative is located",
        rw: "Akarere ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/districts/?province_id={Idprovince}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Iddistrict", visible: false },
    {
      name: "sector",
      type: "dropdown",
      title: {
        en: "Sector where the cooperative is located",
        rw: "Umurenge ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/sectors/?district_id={Iddistrict}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Idsector", visible: false },
    {
      name: "cell",
      type: "dropdown",
      title: {
        en: "Cell where the cooperative is located",
        rw: "Akagari ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/cells/?sector_id={Idsector}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Idcell", visible: false },
    {
      name: "village",
      type: "dropdown",
      title: {
        en: "Village where the cooperative is located",
        rw: "Umudugudu ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/villages/?cell_id={Idcell}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    {
      type: "text",
      name: "president_name",
      title: {
        en: "Name of the president of the cooperative",
        rw: "Izina rya perezida w'ishyirahamwe",
      },
    },
    {
      type: "phone",
      name: "president_phone",
      title: {
        en: "Phone number of the president of the cooperative",
        rw: "Nimero ya telefone ya perezida w'ishyirahamwe",
      },
      validators: [
        expressions.phone_en("president_phone"),
        expressions.phone_length("president_phone"),
      ],
    },
    {
      type: "text",
      name: "email",
      inputType: "email",
      title: {
        en: "E-mail address of the cooperative",
        rw: "Aderesi ya imeri y'ishyirahamwe",
      },
    },
    {
      type: "text",
      name: "members",
      inputType: "number",
      title: {
        en: "Total number of members of the cooperative",
        rw: "Umubare wose w'abanyamuryango b'ishyirahamwe",
      },
      validators: [regexValidators.integer()],
    },
    {
      type: "text",
      name: "members_men",
      inputType: "number",
      title: { en: "Of which men", rw: "Ku bo bagabo" },
      validators: [
        regexValidators.integer(),
        expressions.max_members("members_men"),
      ],
    },
    {
      type: "text",
      name: "members_women",
      inputType: "number",
      title: { en: "Of which women", rw: "Ku bo bagore" },
      validators: [
        regexValidators.integer(),
        expressions.max_members("members_women"),
      ],
    },
    {
      type: "text",
      name: "members_youth",
      inputType: "number",
      title: {
        en: "Number of youth members (<35)",
        rw: "Umubare w'urubyiruko (<35)",
      },
      validators: [
        regexValidators.integer(),
        expressions.max_members("members_youth"),
      ],
    },
    {
      type: "text",
      name: "members_disability",
      inputType: "number",
      title: {
        en: "Number of people with a disability",
        rw: "Umubare w'abafite ubumuga",
      },
      validators: [
        regexValidators.integer(),
        expressions.max_members("members_disability"),
      ],
    },
    {
      type: "comment",
      name: "description",
      title: {
        en: "Observations and comments",
        rw: "Ibitekerezo n'ibisobanuro",
      },
    },
  ],
});

const productionValueChain = () => ({
  name: "page2",
  title: {
    en: "Production & Value Chain",
    rw: "Umusaruro n'urwego rw'agaciro",
  },
  elements: [
    {
      type: "checkbox",
      name: "seasons",
      title: {
        en: "During which seasons do cooperative members cultivate crops?",
        rw: "Mu bihembwe ihe abanyamuryango b'ishyirahamwe bahinga?",
      },
      choices: [
        choice("season_a", "A (September - January)"),
        choice("season_b", "B (February - June)"),
        choice("season_c", "C (June - September)"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "start_season_a",
      visibleIf: "{seasons} contains 'season_a'",
      title: {
        en: "What is the typical start of the planting period for Season A?",
        rw: "Ni ryari ubusanzwe bwo gutangira gutera mu gihembwe A?",
      },
      choices: [
        choice("late_august", "Late August"),
        choice("early_september", "Early September"),
        choice("late_september", "Late September"),
        choice("early_october", "Early October"),
        choice("late_october", "Late October"),
        choice("early_november", "Early November"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "crops_season_a",
      visibleIf: "{seasons} contains 'season_a'",
      title: {
        en: "Which crops are cultivated by cooperative members during Season A?",
        rw: "Ni ibihe bihingwa abanyamuryango bahinga mu gihembwe A?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "harvest_season_a",
      visibleIf: "{seasons} contains 'season_a'",
      title: {
        en: "What is the typical start of the harvest of Season A?",
        rw: "Ni ryari ubusanzwe bwo gutangira gusarura mu gihembwe A?",
      },
      choices: [
        choice("december", "December"),
        choice("january", "January"),
        choice("february", "February"),
        choice("march", "March"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "start_season_b",
      visibleIf: "{seasons} contains 'season_b'",
      title: {
        en: "What is the typical start of the planting period for Season B?",
        rw: "Ni ryari ubusanzwe bwo gutangira gutera mu gihembwe B?",
      },
      choices: [
        choice("late_january", "Late January"),
        choice("early_february", "Early February"),
        choice("late_february", "Late February"),
        choice("early_march", "Early March"),
        choice("late_march", "Late March"),
        choice("early_april", "Early April"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "crops_season_b",
      visibleIf: "{seasons} contains 'season_b'",
      title: {
        en: "Which crops are cultivated by cooperative members during Season B?",
        rw: "Ni ibihe bihingwa abanyamuryango bahinga mu gihembwe B?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "harvest_season_b",
      visibleIf: "{seasons} contains 'season_b'",
      title: {
        en: "What is the typical start of the harvest of Season B?",
        rw: "Ni ryari ubusanzwe bwo gutangira gusarura mu gihembwe B?",
      },
      choices: [
        choice("may", "May"),
        choice("june", "June"),
        choice("july", "July"),
        choice("august", "August"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "start_season_c",
      visibleIf: "{seasons} contains 'season_c'",
      title: {
        en: "What is the typical start of the planting period for Season C?",
        rw: "Ni ryari ubusanzwe bwo gutangira gutera mu gihembwe C?",
      },
      choices: [
        choice("late_may", "Late May"),
        choice("early_june", "Early June"),
        choice("late_june", "Late June"),
        choice("early_july", "Early July"),
        choice("late_july", "Late July"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "crops_season_c",
      visibleIf: "{seasons} contains 'season_c'",
      title: {
        en: "Which crops are cultivated by cooperative members during Season C?",
        rw: "Ni ibihe bihingwa abanyamuryango bahinga mu gihembwe C?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "harvest_season_c",
      visibleIf: "{seasons} contains 'season_c'",
      title: {
        en: "What is the typical start of the harvest of Season C?",
        rw: "Ni ryari ubusanzwe bwo gutangira gusarura mu gihembwe C?",
      },
      choices: [
        choice("august", "August"),
        choice("september", "September"),
        choice("october", "October"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "main_crop",
      title: {
        en: "What is the main crop cultivated by cooperative members?",
        rw: "Ni iki gihingwa nyamukuru cy'ishyirahamwe?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "matrixdropdown",
      name: "total_produce_last_season",
      visibleIf: "{crops_season_a} notempty or {crops_season_b} notempty or {crops_season_c} notempty or {main_crop} notempty",
      title: {
        en: "What was the total volume of produce delivered by cooperative members during the last season?",
        rw: "Ni iyihe ngingo y'umusaruro watanzwe n'abanyamuryango mu gihembwe gishize?",
      },
      columns: [
        {
          name: "tonnes_last_season",
          title: {
            en: "Tonnes last season",
            rw: "Tone mu gihembwe gishize",
          },
          cellType: "text",
          inputType: "number",
          isRequired: required,
          validators: [expressions.non_negative_number()],
        },
      ],
      rows: cropChoices,
      rowsVisibleIf: "{crops_season_a} contains {item} or {crops_season_b} contains {item} or {crops_season_c} contains {item} or {main_crop} contains {item}",
    },
    {
      type: "text",
      name: "land_of_cooperative",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "How many hectares of land are owned by the cooperative?",
        rw: "Ni hegitari zingahe z'ubutaka ishyirahamwe rifite?",
      },
    },
    {
      type: "text",
      name: "land_owners",
      inputType: "number",
      title: {
        en: "How many cooperative members own agricultural land?",
        rw: "Ni abanyamuryango bangahe bafite ubutaka bw'ubuhinzi?",
      },
      validators: [
        regexValidators.integer(),
        expressions.max_members("land_owners"),
      ],
    },
    {
      type: "text",
      name: "land_size_of_members",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What is the estimated total cultivated land of cooperative members?",
        rw: "Ni hegitari zingahe z'ubutaka bw'ubuhinzi bw'abanyamuryango bose?",
      },
    },
    {
      type: "text",
      name: "cost_of_production",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What is the estimated cost of production per hectare for the main crop?",
        rw: "Ni amafaranga angahe y'igishoro ku hegitari ku gihingwa nyamukuru?",
      },
    },
    {
      type: "text",
      name: "revenue_per_hectare",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What is the estimated revenue per hectare for the main crop?",
        rw: "Ni amafaranga angahe yinjiye ku hegitari ku gihingwa nyamukuru?",
      },
    },
  ],
});

const marketAccess = () => ({
  name: "page3",
  title: { en: "Market Access", rw: "Kugera ku isoko" },
  elements: [
    {
      type: "dropdown",
      name: "produce_collection_by_cooperative",
      title: {
        en: "Does the cooperative collect members' produce and sell it to buyers on their behalf?",
        rw: "Ese ishyirahamwe rikusanya umusaruro w'abanyamuryango rigatugisha abaguzi ku bwabo?",
      },
      choices: [
        choice("all_all", "Yes, all produce for all members"),
        choice("some_all", "Yes, some produce for all members"),
        choice("all_some", "Yes, all produce for some members"),
        choice("some_some", "Yes, some produce for some members"),
        choice("no", "No"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "payments_produce",
      title: {
        en: "How are payments for produce typically made?",
        rw: "Ubusanzwe kwishyurwa umusaruro bikorwa gute?",
      },
      choices: [
        choice("cooperative_pays", "The cooperative receives payment from buyers and pays members"),
        choice("buyers_pay_members", "Buyers pay members directly"),
        choice("both", "Both arrangements are used"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "text",
      name: "largest_buyer_percentage",
      inputType: "number",
      min: 0,
      max: 100,
      title: {
        en: "What percentage of sales is made to the largest buyer?",
        rw: "Ni irihe percent by'agurishwa ku muguzi mukuru?",
      },
    },
    {
      type: "dropdown",
      name: "buyer_agreements",
      title: {
        en: "What type of agreements does the cooperative have with its buyers?",
        rw: "Ni ubuhe bwoko bw'amasezerano ishyirahamwe rifite n'abaguzi bayo?",
      },
      choices: [
        choice("no_formal", "No formal agreements"),
        choice("verbal_only", "Verbal agreements only"),
        choice("written_some", "Written agreements with some buyers"),
        choice("written_most", "Written agreements with most buyers"),
      ],
    },
    {
      type: "checkbox",
      name: "buyer_types",
      title: {
        en: "What types of buyers does the cooperative sell to?",
        rw: "Ni ubuhe bwoko bw'abaguzi ishyirahamwe rigurishaho?",
      },
      choices: [
        choice("exporters", "Exporters"),
        choice("agro_processing", "Agro-processing companies"),
        choice("inputs_suppliers", "Inputs suppliers"),
        choice("traders_wholesalers", "Traders, wholesalers"),
        choice("big_farmers", "Big farmers"),
        choice("hotels_restaurants", "Hotels, restaurants, caterers"),
        choice("retailers", "Retailers, local market buyers"),
        choice("institutional", "Institutional buyers (schools, hospitals, government)"),
        choice("individual_consumers", "Individual consumers, neighbours, friends"),
        choice("refugee_humanitarian", "Refugee camps, humanitarian organizations"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "unable_to_sell",
      title: {
        en: "How often is the cooperative unable to sell all produce produced by its members?",
        rw: "Kukihe gihe ishyirahamwe ritagurisha umusaruro wose w'abanyamuryango?",
      },
      choices: [
        choice("very_often", "Very often"),
        choice("often", "Often"),
        choice("sometimes", "Sometimes"),
        choice("rarely", "Rarely"),
        choice("never", "Never"),
      ],
    },
    {
      type: "text",
      name: "rejection_percentage",
      inputType: "number",
      min: 0,
      max: 100,
      title: {
        en: "What was the approximate percentage of rejection of produce during the last season?",
        rw: "Ni irihe percent ry'umusaruro wanzwe mu gihembwe gishize?",
      },
    },
    {
      type: "dropdown",
      name: "reason_for_rejection",
      title: {
        en: "What is the primary reason for produce rejection?",
        rw: "Ni iki cy'ingenzi cyatumye umusaruro unzwe?",
      },
      choices: [
        choice("poor_quality", "Poor quality"),
        choice("moisture_content", "Moisture content"),
        choice("pest_disease", "Pest or disease damage"),
        choice("buyer_specs", "Failure to meet buyer specifications"),
        choice("oversupply", "Oversupply"),
        choice("storage_issues", "Storage issues"),
        choice("transport_damage", "Transport damage"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
  ],
});

const operationalMaturity = () => ({
  name: "page4",
  title: { en: "Operational Maturity", rw: "Ubushobozi bwo gukora" },
  elements: [
    {
      type: "text",
      name: "permanent_staff",
      inputType: "number",
      validators: [regexValidators.integer()],
      title: {
        en: "What is the number of permanent staff members?",
        rw: "Ni abakozi bangahe b'igihe kirekire?",
      },
    },
    {
      type: "text",
      name: "years_active",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "How many years is the cooperative active?",
        rw: "Ni imyaka ingahe ishyirahamwe rikora?",
      },
    },
    {
      type: "dropdown",
      name: "sales_records",
      title: {
        en: "What transaction records does the cooperative maintain for sales to buyers?",
        rw: "Ni ayahe makuru y'ubucuruzi ishyirahamwe ritunga ku kugurisha abaguzi?",
      },
      choices: [
        choice("no_records", "No records"),
        choice("sales_only", "Sales records only"),
        choice("production_only", "Production records only"),
        choice("both", "Both production and sales records"),
        choice("both_linked", "Both production and sales records linked to individual members"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "data_recording",
      title: {
        en: "Which data does the cooperative record for individual members?",
        rw: "Ni ayahe makuru ishyirahamwe ritunga ku banyamuryango ku giti cyabo?",
      },
      choices: [
        choice("none", "None"),
        choice("production_data", "Production data"),
        choice("sales_data", "Sales data"),
        choice("land_size", "Land size"),
        choice("productivity", "Productivity"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "record_keeping",
      title: {
        en: "How are these records maintained?",
        rw: "Aya makuru atungwa ate?",
      },
      choices: [
        choice("no_records", "No records"),
        choice("basic_handwritten", "Basic handwritten"),
        choice("organized_paper", "Organized paper records"),
        choice("sheets_excel", "Sheets / Excel"),
        choice("dedicated_software", "Dedicated software"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "data_verification",
      title: {
        en: "Did a MoneyPhone loan officer verify the production and sales records?",
        rw: "Ese umukozi w'inguzanyo wa MoneyPhone yemeje inyandiko z'umusaruro n'ubucuruzi?",
      },
      choices: [
        choice("no_records", "No records available"),
        choice("production_verified", "Production records verified"),
        choice("sales_verified", "Sales records verified"),
        choice("both_verified", "Both production and sales records verified"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
  ],
});

const financialPerformance = () => ({
  name: "page5",
  title: { en: "Financial Performance", rw: "Imikorere y'imari" },
  elements: [
    {
      type: "checkbox",
      name: "sources_of_income",
      title: {
        en: "What are the cooperative's main sources of income?",
        rw: "Ni ayahe maturutse y'inyungu y'ishyirahamwe?",
      },
      choices: [
        choice("crop_aggregation", "Crop aggregation / trading (Agrodealer)"),
        choice("seed_multiplication", "Seed multiplication"),
        choice("input_sales", "Input sales"),
        choice("agro_processing", "Agro-processing"),
        choice("transport_services", "Transport services"),
        choice("equipment_rental", "Equipment rental"),
        choice("hospitality_services", "Hospitality services"),
        choice("membership_fees", "Membership fees"),
        choice("commissions", "Commissions"),
        choice("grants_subsidies", "Grants/subsidies"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "text",
      name: "revenue_last_year",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What was the cooperative's estimated revenue last year?",
        rw: "Ni amafaranga angahe yinjiye ishyirahamwe mu mwaka ushize?",
      },
    },
    {
      type: "text",
      name: "expenses_last_year",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What were the cooperative's estimated expenses last year?",
        rw: "Ni amafaranga angahe yakoreshejwe n'ishyirahamwe mu mwaka ushize?",
      },
    },
    {
      type: "text",
      name: "revenue_two_years_ago",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What was the cooperative's estimated revenue two years ago?",
        rw: "Ni amafaranga angahe yinjiye ishyirahamwe mu myaka ibiri ishize?",
      },
    },
    {
      type: "text",
      name: "expenses_two_years_ago",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What were the cooperative's estimated expenses two years ago?",
        rw: "Ni amafaranga angahe yakoreshejwe n'ishyirahamwe mu myaka ibiri ishize?",
      },
    },
    {
      type: "checkbox",
      name: "loan_usage",
      title: {
        en: "What are the intended uses of the members' loans?",
        rw: "Ni iki inguzanyo z'abanyamuryango zigamije gukoreshwa?",
      },
      choices: [
        choice("seeds", "Seeds"),
        choice("fertilizers", "Fertilizers"),
        choice("crop_protection", "Crop protection products"),
        choice("labor", "Labor"),
        choice("land_preparation", "Land preparation"),
        choice("land_expansion", "Land expansion / leasing additional land"),
        choice("irrigation_equipment", "Irrigation equipment"),
        choice("post_harvest", "Post-harvest infrastructure (drying, storage, processing)"),
        choice("transport_logistics", "Transport and logistics"),
        choice("farm_equipment", "Farm equipment and machinery"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "loan_size",
      title: {
        en: "What loan amount do cooperative members consider most suitable for their farming activities?",
        rw: "Ni inguzanyo ihe abanyamuryango batekereza ko ikwiye ku buhinzi bwabo?",
      },
      choices: [
        choice("lt_100k", "< 100K"),
        choice("100k_500k", "100K-500K"),
        choice("500k_1m", "500K-1M"),
        choice("1m_2m", "1-2M"),
        choice("gt_2m", "> 2M"),
      ],
    },
    {
      type: "dropdown",
      name: "guarantor_willingness",
      title: {
        en: "Is the cooperative willing to act as a guarantor for member loans?",
        rw: "Ese ishyirahamwe ryiteguye kuba ingwate y'inguzanyo z'abanyamuryango?",
      },
      choices: [
        choice("no", "No"),
        choice("yes_conditions", "Yes with specific conditions"),
        choice("yes_selected", "Yes, for selected members only"),
        choice("yes_all", "Yes, for all eligible members"),
      ],
    },
    {
      type: "dropdown",
      name: "guarantor_experience",
      title: {
        en: "Has the cooperative previously guaranteed loans for its members?",
        rw: "Ese ishyirahamwe ryigeze kuba ingwate y'inguzanyo z'abanyamuryango?",
      },
      choices: [
        choice("never", "Never"),
        choice("rarely", "Rarely"),
        choice("regularly", "Regularly"),
      ],
    },
  ],
});

const governanceCompliance = () => ({
  name: "page6",
  title: { en: "Governance & Compliance", rw: "Imiyoborere n'ubwitange" },
  elements: [
    {
      type: "text",
      name: "board_meetings",
      inputType: "number",
      validators: [regexValidators.integer()],
      title: {
        en: "How many board meetings were held in the last 12 months?",
        rw: "Ni inama zingahe z'ikomite y'ubuyobozi zabereye mu mezi 12 ashize?",
      },
    },
    {
      type: "text",
      name: "general_assembly_meetings",
      inputType: "number",
      validators: [regexValidators.integer()],
      title: {
        en: "How many general assembly meetings were held in the last 12 months?",
        rw: "Ni inama zingahe z'ishyirahamwe rusange zabereye mu mezi 12 ashize?",
      },
    },
    {
      type: "text",
      name: "audit_committee_meetings",
      inputType: "number",
      validators: [regexValidators.integer()],
      title: {
        en: "How many audit committee meetings were held in the last 12 months?",
        rw: "Ni inama zingahe z'ikomite isuzuma zabereye mu mezi 12 ashize?",
      },
    },
    {
      type: "text",
      name: "attendance_rate_general_assembly",
      inputType: "number",
      min: 0,
      max: 100,
      title: {
        en: "What is the average attendance rate of the general assembly meetings in the past 12 months?",
        rw: "Ni irihe percent ry'abitabiriye inama z'ishyirahamwe rusange mu mezi 12 ashize?",
      },
    },
  ],
});

const climateInsuranceIrrigation = () => ({
  name: "page7",
  title: {
    en: "Climate, Insurance and Irrigation",
    rw: "Ibihe, ubwishingizi n'ubuhinzi bw'ubwoba",
  },
  elements: [
    {
      type: "boolean",
      name: "climate_events",
      title: {
        en: "Has the cooperative area experienced any significant weather-related events during the past 2 years?",
        rw: "Ese akarere k'ishyirahamwe kigeze kubona ibihe bidasanzwe mu myaka 2 ishize?",
      },
      labelTrue: { en: "Yes", rw: "Yego" },
      labelFalse: { en: "No", rw: "Oya" },
    },
    {
      type: "dropdown",
      name: "climate_impact",
      visibleIf: "{climate_events} = true",
      title: {
        en: "What was the impact of weather related events on production during the past 2 years?",
        rw: "Ni irihe tandukaniro ry'ibihe bidasanzwe byagize ku musaruro mu myaka 2 ishize?",
      },
      choices: [
        choice("no_impact", "No impact"),
        choice("minor", "Minor, small reduction in production"),
        choice("moderate", "Moderate, noticeable reduction in production"),
        choice("significant", "Significant, major reduction affecting most farmers"),
        choice("severe", "Severe, crop failure or major losses"),
      ],
    },
    {
      type: "text",
      name: "members_with_irrigation",
      inputType: "number",
      title: {
        en: "How many cooperative members have access to irrigation systems?",
        rw: "Ni abanyamuryango bangahe bafite uburyo bwo gukoresha uburyo bwo kuhira?",
      },
      validators: [
        regexValidators.integer(),
        expressions.max_members("members_with_irrigation"),
      ],
    },
    {
      type: "text",
      name: "members_with_crop_insurance",
      inputType: "number",
      title: {
        en: "How many cooperative members currently have crop insurance?",
        rw: "Ni abanyamuryango bangahe bafite ubwishingizi bw'ibihingwa?",
      },
      validators: [
        regexValidators.integer(),
        expressions.max_members("members_with_crop_insurance"),
      ],
    },
  ],
});

const financialAccess = () => ({
  name: "page8",
  title: { en: "Financial Access", rw: "Kugera ku mafaranga" },
  elements: [
    {
      type: "text",
      name: "access_to_credit",
      inputType: "number",
      title: {
        en: "How many cooperative members currently have access to formal credit?",
        rw: "Ni abanyamuryango bangahe bafite uburyo bwo kubona inguzanyo zemewe?",
      },
      validators: [
        regexValidators.integer(),
        expressions.max_members("access_to_credit"),
      ],
    },
    {
      type: "checkbox",
      name: "credit_sources",
      title: {
        en: "What are the main sources of formal credit available to cooperative members?",
        rw: "Ni ayahe maturutse y'inguzanyo zemewe abanyamuryango bashobora kubona?",
      },
      choices: [
        choice("saccos", "SACCOs"),
        choice("banks", "Banks"),
        choice("mfis", "MFIs"),
        choice("savings_groups", "Savings groups"),
        choice("input_suppliers", "Input suppliers"),
        choice("buyers_contract_farming", "Buyers / Contract Farming Arrangements"),
        choice("government_programs", "Government programs"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "text",
      name: "interest_rate",
      inputType: "number",
      min: 0,
      max: 100,
      title: {
        en: "What is the average annual interest rate charged by the financial institution most commonly used by cooperative members?",
        rw: "Ni irihe percent ry'inyungu ku mwaka ryishyurwa n'ikigo cy'imari abanyamuryango bakoresha cyane?",
      },
    },
    {
      type: "text",
      name: "turnaround_time",
      inputType: "number",
      min: 0,
      max: 365,
      title: {
        en: "What is the average loan turnaround time (from application to disbursement) of that financial institution?",
        rw: "Ni iminsi ingahe hagati yo gusaba no kubona inguzanyo muri icyo kigo cy'imari?",
      },
    },
    {
      type: "dropdown",
      name: "challenges_loan_access",
      title: {
        en: "What are the main challenges cooperative members face when accessing loans?",
        rw: "Ni ibihe bibazo bikuru abanyamuryango bahura nabyo mu kubona inguzanyo?",
      },
      choices: [
        choice("high_interest", "High interest rates"),
        choice("collateral", "Collateral requirements"),
        choice("long_turnaround", "Long turnaround time"),
        choice("small_amounts", "Small loan amounts"),
        choice("complex_procedures", "Complex procedures"),
        choice("short_repayment", "Short repayment periods"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
  ],
});

const supportingDocuments = () => ({
  name: "page9",
  title: { en: "Supporting Documents", rw: "Inyandiko zishyigikira" },
  elements: [
    {
      type: "file_uploader",
      name: "rca_certificate",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload a copy of the Legal Registration Document",
        rw: "Shyiraho kopi y'inyandiko y'iyandikisha",
      },
    },
    {
      type: "file_uploader",
      name: "members_list",
      isRequired: true,
      requiredErrorText: {
        en: "Please upload all required documents before submitting.",
        rw: "Nyamuneka shyiraho inyandiko zose zisabwa mbere yo kohereza.",
      },
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest version of the list of membership *",
        rw: "Shyiraho urutonde rw'ubu rw'abanyamuryango *",
      },
    },
    {
      type: "file_uploader",
      name: "financial_reports",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest financial reports",
        rw: "Shyiraho raporo y'imari iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "contract",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload contracts with buyers if any",
        rw: "Shyiraho amasezerano n'abaguzi niba ahari",
      },
    },
    {
      type: "file_uploader",
      name: "business_records",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload business records (production, sales, transaction records, balance sheets, etc. if available)",
        rw: "Shyiraho inyandiko z'ubucuruzi (umusaruro, ubucuruzi, inyandiko z'ubucuruzi, balance sheets, n'ibindi niba bihari)",
      },
    },
    {
      type: "file_uploader",
      name: "assembly_attendance",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the list of attendance of the latest general assembly meeting",
        rw: "Shyiraho urutonde rw'abitabiriye inama y'ishyirahamwe rusange iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "general_assembly_minutes",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest meeting minutes of the general assembly meetings",
        rw: "Shyiraho inyandiko z'inama y'ishyirahamwe rusange iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "audit_committee_minutes",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest meeting minutes and reports of the auditing committee meetings",
        rw: "Shyiraho inyandiko n'raporo z'ikomite isuzuma iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "relevant_documents",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload procedures, manuals or other relevant documents",
        rw: "Shyiraho amabwiriza, inyandiko cyangwa izindi nyandiko z'ingirakamaro",
      },
    },
    {
      type: "file_uploader",
      name: "insurance_contract",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the contract with the insurance company, if any",
        rw: "Shyiraho amasezerano n'ikigo cy'ubwishingizi niba ahari",
      },
    },
  ],
});

export const cooperative_assessment_rwanda_direct_model = (clientUrlForCatalog) => ({
  showProgressBar: "top",
  showQuestionNumbers: "off",
  sendResultOnPageNext: true,
  firstPageIsStarted: false,
  showCompletedPage: false,
  checkErrorsMode: "onNextPage",
  completeText: { en: "Submit assessment", rw: "Ohereza isuzuma" },
  pageNextText: translate("general.pageNextText"),
  pagePrevText: translate("general.pagePrevText"),
  requiredText: "",
  locale: "en",
  pages: [
    generalInformation(clientUrlForCatalog),
    productionValueChain(),
    marketAccess(),
    operationalMaturity(),
    financialPerformance(),
    governanceCompliance(),
    climateInsuranceIrrigation(),
    financialAccess(),
    supportingDocuments(),
  ],
});
